"use client"

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ExternalLink } from 'lucide-react'
import { AdConfig, getAdvertiserDisplayName, getAdvertiserDescription } from '@/lib/ad-config'

interface AdCardProps extends AdConfig {
  className?: string
  variant?: 'blog' | 'collection'
  needsWhiteBg?: boolean
}

/**
 * Ad Card Component - Two variants:
 * - blog: Compact with full-height image for blog sidebars
 * - collection: Matches product cards for collection grids (default)
 *
 * Real-or-Hidden (FRIDGE-621, 08/29/2026): an ad whose creative cannot load —
 * ad-blocker, dead CDN, missing imageUrl — renders NOTHING. The previous
 * behavior swapped in a generic icon-and-CTA placeholder, which is exactly
 * the "broken ad" box the owner saw on every ad surface while the CSP
 * blocked the creative hosts. A slot with no fill does not exist.
 */

export function AdCard({ 
  network,
  advertiser,
  trackingUrl, 
  imageUrl, 
  imageAlt,
  className = '',
  variant = 'collection',
  needsWhiteBg = false
}: AdCardProps) {
  const [imageError, setImageError] = useState(false)
  const advertiserName = getAdvertiserDisplayName(advertiser)
  const advertiserDesc = getAdvertiserDescription(advertiser)

  const handleClick = async () => {
    // Track ad click
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
      try {
        await fetch('/api/ad-clicks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            network,
            advertiser,
            size: '300x250',
            timestamp: new Date().toISOString(),
            page: window.location.pathname
          })
        })
      } catch (error) {
        // Silently handle - ad tracking is optional
      }
    }
  }

  // Real-or-Hidden: no creative, no slot. Never render a placeholder box.
  if (!imageUrl || imageError) {
    return null
  }

  // BLOG VARIANT - Compact with full-height image, entire card is clickable
  if (variant === 'blog') {
    return (
      <a 
        href={trackingUrl}
        target="_blank"
        rel="nofollow noopener sponsored"
        onClick={handleClick}
        className={`block ${className}`}
      >
        <div className="group hover:shadow-lg transition-shadow rounded-lg border bg-white overflow-hidden cursor-pointer">
          {/* IMAGE CONTAINER */}
          <div className="relative">
            <div className={`overflow-hidden relative border-b border-gray-200 flex items-center justify-center ${needsWhiteBg ? 'bg-white' : 'bg-gray-50'}`} style={{ minHeight: '180px' }}>
              {/* Ad Badge */}
              <div className="absolute top-2 left-2 z-10 bg-white bg-opacity-95 px-1.5 py-0.5 rounded shadow-sm">
                <p className="text-gray-600 text-[10px] font-medium">Ad</p>
              </div>
              
              <img
                src={imageUrl}
                alt={imageAlt || advertiserName}
                className="w-full h-full object-contain"
                onError={() => setImageError(true)}
              />
            </div>
          </div>

          {/* CONTENT - Tight padding like BlogPicks cards */}
          <div className="px-2 py-2">
            <h3 className="font-semibold text-sm leading-tight line-clamp-1">
              {advertiserName}
            </h3>
            
            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
              {advertiserDesc}
            </p>

            <div className="mt-2">
              <Button variant="ctaPrimary" className="w-full h-8 text-xs font-semibold">
                <ExternalLink className="w-3 h-3 mr-1" />
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </a>
    )
  }

  // COLLECTION VARIANT - Matches product cards exactly, entire card is clickable
  return (
    <a 
      href={trackingUrl}
      target="_blank"
      rel="nofollow noopener sponsored"
      onClick={handleClick}
      className={`block h-full ${className}`}
    >
      <Card className="group hover:shadow-lg transition-shadow pt-2 sm:pt-3 px-0 pb-0 gap-0 flex flex-col h-full cursor-pointer">
        {/* IMAGE CONTAINER - Square like products */}
        <div className="relative">
          <div className={`aspect-square overflow-hidden rounded-t-lg relative border-b border-gray-200 flex items-center justify-center ${needsWhiteBg ? 'bg-white' : 'bg-gray-50'}`}>
            {/* Ad Badge */}
            <div className="absolute top-2 left-2 z-10 bg-white bg-opacity-95 px-2 py-0.5 rounded shadow-sm">
              <p className="text-gray-600 text-xs font-medium">Ad</p>
            </div>
            
            <img
              src={imageUrl}
              alt={imageAlt || advertiserName}
              className="w-full h-full object-contain"
              onError={() => setImageError(true)}
            />
          </div>
        </div>

        {/* CONTENT - Matches product card layout */}
        <CardContent className="px-3 pt-2 pb-3 flex flex-col justify-between h-full">
          <div className="flex-1 flex flex-col">
            {/* Title - Allow full text, match product card positioning */}
            <h3 className="font-semibold text-sm sm:text-base mb-1.5 leading-tight">
              {advertiserName}
            </h3>
            
            {/* Description */}
            <p className="text-xs text-gray-600 mb-2 line-clamp-3">
              {advertiserDesc}
            </p>
          </div>

          {/* BUTTON - Bottom aligned like products */}
          <div className="flex flex-col gap-2 mt-auto mt-3">
            <Button variant="ctaPrimary" className="w-full h-10 text-sm font-semibold min-h-[44px]">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              Learn More
            </Button>
          </div>
        </CardContent>
      </Card>
    </a>
  )
}

/**
 * Ad Grid - Display multiple ad cards in a grid
 */
interface AdGridProps {
  ads: AdConfig[]
  columns?: 2 | 3 | 4
  className?: string
}

export function AdCardGrid({ ads, columns = 4, className = '' }: AdGridProps) {
  const gridClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  }

  return (
    <div className={`grid ${gridClasses[columns]} gap-6 ${className}`}>
      {ads.map((ad, index) => (
        <AdCard key={index} {...ad} />
      ))}
    </div>
  )
}
